import React, {FC} from "react"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import {PerkboxLogo, SmallPerkboxLogo} from "@irati/ui/src/Logos"
import Flex from "@irati/ui/src/Flex"
import {Label} from "@irati/ui/src/Typography"
import {IconTypes} from "@irati/ui/src/Icon"
import adminTracking from "@irati-tracking/admin"
import {IconButton} from "@irati/ui/src/Button"
import authentication from "@irati/auth"
import {
  ADMIN_HOME,
  VIEW_PEOPLE,
  VIEW_TEAM,
  VIEW_BUSINESS_UNITS,
  SETTINGS_CUSTOMISE,
  SETTINGS_PERMISSIONS_RECOGNITION,
  SETTINGS_LOGIN_INTEGRATION,
  HELP,
  BILLING,
  AUTOMATION_BASE,
  ANALYTICS_BASE,
  ORDER_MANAGEMENT
} from "@irati-web/admin/src/routes/routeConfig"

import {StyledBusinessLogoWrapper, StyledLogo} from "./styled"
import AdminRouterLink from "./AdminRouterLink"
import RewardsAdminRouterLink from "./RewardsAdminRouterLink"
import devToggles from "@irati/dev-toggles"

interface Props extends InjectIntl {
  onClick?: () => void
  businessLogoUrl?: string
  isCollapsed?: boolean
}

const AdminNavigationMenuContent: FC<Props> = ({formatMessage, isCollapsed, onClick, businessLogoUrl}) => {
  return (
    <>
      <StyledBusinessLogoWrapper>
        <StyledLogo src={businessLogoUrl} isCollapsed={isCollapsed} alt="" />
      </StyledBusinessLogoWrapper>
      <Flex direction="column" alignItems={isCollapsed ? "center" : "stretch"} padding={[0, 2, 0, 2]}>
        <AdminRouterLink
          id="homePageButton"
          isCollapsed={isCollapsed}
          to={ADMIN_HOME}
          icon={IconTypes.adminHome}
          onClick={adminTracking.navigationClickHome}
          title="platform.adminNavigation.primary.home"
          aria-label="navigation link button to home section"
        />
        <AdminRouterLink
          id="peoplePageButton"
          isCollapsed={isCollapsed}
          to={VIEW_PEOPLE}
          icon={IconTypes.searchPeople}
          onClick={adminTracking.navigationClickPeople}
          title="platform.adminNavigation.primary.people"
          aria-label="navigation link button to people section"
        />
        <AdminRouterLink
          id="teamsPageButton"
          isCollapsed={isCollapsed}
          to={VIEW_TEAM}
          icon={IconTypes.teams}
          onClick={adminTracking.navigationClickTeams}
          title="platform.adminNavigation.primary.teams"
          aria-label="navigation link button to teams section"
        />
        <AdminRouterLink
          id="countriesPageButton"
          isCollapsed={isCollapsed}
          to={VIEW_BUSINESS_UNITS}
          icon={IconTypes.countries}
          onClick={adminTracking.navigationClickCountries}
          title="platform.adminNavigation.primary.countries"
          aria-label="navigation link button to countries section"
        />
        <AdminRouterLink
          id="reportingPageButton"
          isCollapsed={isCollapsed}
          to={ANALYTICS_BASE}
          icon={IconTypes.analytics}
          onClick={adminTracking.navigationClickReporting}
          title="platform.adminNavigation.primary.analytics"
          aria-label="navigation link button to reporting section"
        />
        <AdminRouterLink
          id="customisationPageButton"
          isCollapsed={isCollapsed}
          to={SETTINGS_CUSTOMISE}
          icon={IconTypes.customisation}
          onClick={adminTracking.navigationClickCustomisations}
          title="platform.adminNavigation.primary.customisation"
          aria-label="navigation link button to customise section"
        />
        <AdminRouterLink
          id="automationPageButton"
          isCollapsed={isCollapsed}
          to={AUTOMATION_BASE}
          icon={IconTypes.automation}
          onClick={adminTracking.navigationClickAutomation}
          title="platform.adminNavigation.primary.automation"
          aria-label="navigation link button to automation section"
        />
        {devToggles.showPayrollOrderManagement() && (
          <AdminRouterLink
            id="orderManagementPageButton"
            isCollapsed={isCollapsed}
            to={ORDER_MANAGEMENT}
            icon={IconTypes.orderManagement}
            title="Salary sacrifice orders"
          />
        )}
        {authentication.apps("wallet.flexiRewards") && <RewardsAdminRouterLink isCollapsed={isCollapsed} />}
        <AdminRouterLink
          id="integrationPageButton"
          isCollapsed={isCollapsed}
          to={SETTINGS_LOGIN_INTEGRATION}
          icon={IconTypes.integration}
          onClick={adminTracking.navigationClickIntegrations}
          title="platform.adminNavigation.primary.integration"
          aria-label="navigation link button to integration section"
        />
        <AdminRouterLink
          id="billingPageButton"
          isCollapsed={isCollapsed}
          to={BILLING}
          icon={IconTypes.billing}
          onClick={adminTracking.navigationClickBilling}
          title="platform.adminNavigation.primary.billing"
          aria-label="navigation link button to billing section"
        />
        <AdminRouterLink
          id="settingsPageButton"
          isCollapsed={isCollapsed}
          to={SETTINGS_PERMISSIONS_RECOGNITION}
          icon={IconTypes.settings}
          onClick={adminTracking.navigationClickSettings}
          title="platform.adminNavigation.primary.settings"
          aria-label="navigation link button to setting section"
        />
        <AdminRouterLink
          id="helpPageButton"
          isCollapsed={isCollapsed}
          to={HELP}
          icon={IconTypes.circleQuestion}
          onClick={adminTracking.navigationClickHelp}
          title="platform.adminNavigation.primary.help"
          aria-label="navigation link button to help section"
        />
        <Flex direction="column" marginTop={2}>
          {isCollapsed ? (
            <SmallPerkboxLogo />
          ) : (
            <Flex marginBottom={1}>
              <Label weight="bold" small margin={[0.5, 1, 0, 0]}>
                {formatMessage({id: "platform.ui.poweredBy"})}
              </Label>
              <PerkboxLogo size={20} />
            </Flex>
          )}
          <div>
            <IconButton
              onClick={onClick}
              marginTop={1}
              size={32}
              icon={isCollapsed ? IconTypes.expand : IconTypes.collapse}
              aria-label="Go back button"
            />
          </div>
        </Flex>
      </Flex>
    </>
  )
}

export default injectIntl(AdminNavigationMenuContent)
